// Xem PDF bảng kê. Backend tạo file bất đồng bộ nên phải hỏi lại tới khi xong
// rồi mới tải về — cùng khuôn với AgreementPdfModal đang dùng cho biên bản.
import MessageBox from "components/shared-components/MessageBox";
import ReactPdfViewer from "components/shared-components/ReactPdfViewer";
import {
  useDownloadStatementPdf,
  useStatementPdfStatus,
} from "hooks/invoiceStatement/useInvoiceStatement";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, Spinner } from "reactstrap";
import { STATEMENT_PDF_STATUS_GENERATED } from "../constants";

const DELAY_CHECK_PDF = 3000;
const MAX_CHECK_PDF = 10;

const styleDiv = {
  width: "100%",
  height: "80vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#8080802b",
};

const StatementPdfModal = props => {
  const { isOpen, toggle, statementId, code } = props;

  const [pdfUrl, setPdfUrl] = useState(null);
  const [isError, setIsError] = useState(false);
  const [isWaitTooLong, setIsWaitTooLong] = useState(false);

  const checkCountRef = useRef(0);
  const timerRef = useRef(null);

  const { mutate: mutateDownload } = useDownloadStatementPdf({
    onSuccess: payload => {
      setPdfUrl(URL.createObjectURL(payload.data));
    },
    onError: () => setIsError(true),
  });

  const { mutate: mutateCheckPdf } = useStatementPdfStatus({
    onSuccess: payload => {
      if (payload?.data?.pdf_status === STATEMENT_PDF_STATUS_GENERATED) {
        mutateDownload(statementId);
        return;
      }

      // Chưa xong: chờ rồi hỏi lại, quá số lần cho phép thì để user tự thử lại
      if (checkCountRef.current >= MAX_CHECK_PDF) {
        setIsWaitTooLong(true);
        return;
      }

      checkCountRef.current += 1;
      timerRef.current = setTimeout(
        () => mutateCheckPdf(statementId),
        DELAY_CHECK_PDF
      );
    },
    onError: () => setIsError(true),
  });

  const startCheckPdf = () => {
    clearTimeout(timerRef.current);
    checkCountRef.current = 0;
    setPdfUrl(null);
    setIsError(false);
    setIsWaitTooLong(false);
    mutateCheckPdf(statementId);
  };

  // Mở modal thì hỏi trạng thái file, đóng modal thì dừng và xóa sạch
  useEffect(() => {
    if (isOpen && statementId) {
      startCheckPdf();
    } else {
      clearTimeout(timerRef.current);
      setPdfUrl(null);
      setIsError(false);
      setIsWaitTooLong(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, statementId]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Thu hồi object URL cũ để không rò bộ nhớ
  useEffect(
    () => () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    },
    [pdfUrl]
  );

  return (
    <Modal
      size="xl"
      contentClassName="confirm-modal"
      isOpen={isOpen}
      role="dialog"
      autoFocus
      centered
      toggle={toggle}
    >
      <div className="modal-content">
        <ModalBody>
          {isError ? (
            <div style={styleDiv}>
              <MessageBox
                variant="danger"
                classMore="d-sm-flex align-items-center justify-content-center gap-1 font-size-18"
              >
                <i className="bx bxs-error-alt" />
                Không tải được file bảng kê
              </MessageBox>
            </div>
          ) : isWaitTooLong ? (
            <div style={styleDiv}>
              <MessageBox
                variant="warning"
                classMore="d-sm-flex align-items-center justify-content-center gap-1 font-size-18"
              >
                <i className="bx bx-time-five" />
                File bảng kê đang được tạo, vui lòng thử lại
              </MessageBox>
            </div>
          ) : !pdfUrl ? (
            <div style={styleDiv}>
              <div className="d-flex flex-column align-items-center gap-2">
                <Spinner color="primary" />
                <span>Đang tạo file bảng kê...</span>
              </div>
            </div>
          ) : (
            <ReactPdfViewer filePdfUrl={pdfUrl} style={{ height: "80vh" }} />
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="danger" onClick={toggle}>
            Đóng
          </Button>
          {isWaitTooLong && (
            <Button type="button" color="primary" onClick={startCheckPdf}>
              <i className="bx bx-refresh me-1" />
              Thử lại
            </Button>
          )}
          {pdfUrl && (
            <Button
              type="button"
              color="success"
              onClick={() => {
                const link = document.createElement("a");
                link.href = pdfUrl;
                link.setAttribute("download", `${code || "bang-ke"}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
              }}
            >
              <i className="bx bx-download me-1" />
              Tải xuống
            </Button>
          )}
        </ModalFooter>
      </div>
    </Modal>
  );
};

StatementPdfModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func.isRequired,
  statementId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  code: PropTypes.string,
};

StatementPdfModal.defaultProps = {
  isOpen: false,
  statementId: null,
  code: "",
};

export default StatementPdfModal;
